'use client';

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import { useStepAccess } from '../utils/useStepAccess';

/**
 * Renders nothing; wires the step-access policy into navigation.
 *
 * Two jobs:
 *
 * 1. Registers `canAccessStep` as the guard on `StepNavigationContext`, so *any*
 *    programmatic forward jump — a summary-bar tile, a future caller, draft
 *    auto-navigation — is refused when the steps in between are unfinished.
 *    `StepNavigationProvider` sits above `SchedulerProvider` in the tree and so
 *    cannot read the customer's data itself; injecting the guard from here keeps
 *    the policy in one place without inverting the providers.
 *
 * 2. Reconciles the step the URL asked for (`?step=summary`) with the step the
 *    customer's data actually entitles them to, once the scheduler has settled.
 *
 * The reconciliation runs once, on entry. Later edits that invalidate an earlier
 * step (adding a recommended service at the summary, say) deliberately do NOT
 * yank the customer backwards mid-flow — Schedule Now disables itself and lists
 * what is missing, and the backend refuses the booking regardless.
 */
export default function StepAccessGuard() {
  const searchParams = useSearchParams();
  const { activeStep, setActiveStep, registerStepGuard } = useStepNavigation();
  const { canAccessStep, furthestAllowedStep, isNavigationLocked } =
    useStepAccess();
  const { isLoadingDraft, shouldResumeDraft } = useScheduler();

  // Keep the guard pointed at the current closure so it always validates against
  // the latest data, and drop it on unmount.
  useEffect(() => {
    registerStepGuard(canAccessStep);
    return () => registerStepGuard(null);
  }, [registerStepGuard, canAccessStep]);

  // A `?draft=` deep link hydrates asynchronously; wait for that round trip
  // before judging which steps are complete, otherwise we would reconcile
  // against empty state and then have to navigate a second time.
  const draftRequestedInUrl = !!searchParams.get('draft');
  const observedDraftLoadRef = useRef(false);
  const hasReconciledRef = useRef(false);

  useEffect(() => {
    if (isLoadingDraft) observedDraftLoadRef.current = true;
  }, [isLoadingDraft]);

  useEffect(() => {
    if (hasReconciledRef.current) return;
    if (isNavigationLocked || isLoadingDraft || shouldResumeDraft) return;
    if (draftRequestedInUrl && !observedDraftLoadRef.current) return;

    hasReconciledRef.current = true;

    if (activeStep > furthestAllowedStep) {
      setActiveStep(furthestAllowedStep);
    }
  }, [
    activeStep,
    draftRequestedInUrl,
    furthestAllowedStep,
    isLoadingDraft,
    isNavigationLocked,
    setActiveStep,
    shouldResumeDraft,
  ]);

  return null;
}
